/* eslint-disable no-param-reassign */
import Vue from 'vue'
import { ref, computed } from '@vue/composition-api'

// aggregationsRef ... AggregationFetchService から取得した Aggregation のリスト
export default (aggregationsRef = ref([])) => {
  const sortAndFilterAggregationType = (aggregations, type) => {
    return [...aggregations]
      .filter(aggregation => aggregation && aggregation.attributes && aggregation.attributes.type === type)
      .sort((a, b) => a.attributes.position - b.attributes.position)
  }

  const buildAggregationMap = aggregations => {
    return aggregations.reduce((grouped, aggregation) => {
      if (aggregation && aggregation.attributes) {
        const { id } = aggregation.attributes
        grouped[id] = aggregation
      }

      return grouped
    }, {})
  }

  /**
   * Aggregation の結果を特定するためのユニークなキーを生成します。
   *
   * @param {string} contextType - コンテキストの種類 ("User" または "Club")。
   * @param {number|null} [contextId=null] - コンテキストの ID。Club の場合は null。
   * @param {number} businessRuleId - ビジネスルールの ID。
   * @returns {string} - 生成されたマッピングキー。
   */
  const generateMappingKey = (contextType, contextId = null, businessRuleId) => {
    return `${contextType}-${contextId || 'null'}-${businessRuleId}`
  }

  /**
   * Aggregation の結果を効率的に参照するためのマップを生成します。
   * contextType、contextId、businessRuleId の組み合わせをキーとして、
   * 対応する result.attributes を値とするマップを作成します。
   *
   * @type {import('@vue/composition-api').ComputedRef<Object.<string, Object>>}
   */
  const aggregationResultsMap = computed(() => {
    const resultMap = {}
    aggregationsRef.value.forEach(aggregation => {
      if (aggregation && aggregation.attributes && aggregation.attributes.contexts && aggregation.attributes.contexts.data) {
        aggregation.attributes.contexts.data.forEach(context => {
          if (context && context.attributes) {
            const { id: contextId, contextType } = context.attributes
            if (context.attributes.results && context.attributes.results.data) {
              context.attributes.results.data.forEach(result => {
                if (result && result.attributes) {
                  const { businessRuleId } = result.attributes
                  const key = generateMappingKey(contextType, contextId, businessRuleId)
                  if (!resultMap[key]) resultMap[key] = []
                  resultMap[key].push(result.attributes)
                }
              })
            }
          }
        })
      }
    })

    return resultMap
  })

  /**
   * BusinessRuleResult のデータを使用して、対応する Aggregation の結果を更新します。
   * aggregationResultsMap を利用して効率的に対象を特定し、値を更新します。
   *
   * interface BusinessRuleResult {
   *   id: string;
   *   type: string;
   *   attributes: {
   *     id: number;
   *     businessRuleId: number;
   *     resultValue: number | string | boolean | null;
   *     contextId: number;
   *     contextType: "User" | "Club";
   *     businessDate: string;
   *     createdAt: string;
   *     isDirty: boolean;,
   *   }
   * }
   * @param {BusinessRuleResult} businessRuleResult - 更新に使用する BusinessRuleResult オブジェクト。
   */
  const updateAggregationResult = businessRuleResult => {
    const {
      contextType,
      contextId,
      resultValue,
      businessRuleId,
      id: businessRuleResultId,
    } = businessRuleResult.attributes

    const key = generateMappingKey(contextType, contextId, businessRuleId)
    const targetResults = aggregationResultsMap.value[key]
    if (Array.isArray(targetResults)) {
      targetResults.forEach(targetResultAttributes => {
        Vue.set(targetResultAttributes, 'value', resultValue)
        Vue.set(targetResultAttributes, 'businessRuleResultId', businessRuleResultId)
      })
    }
  }

  return {
    aggregationResultsMap,
    updateAggregationResult,
    sortAndFilterAggregationType,
    buildAggregationMap,
  }
}
